import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useSocio, useUpsertSocio } from '@/lib/hooks';
import { useUIStore } from '@/lib/store';
import { useState, useEffect } from 'react';

export const Route = createFileRoute('/_authed/socios/$slug')({
  component: SocioEditPage,
});

const empty: any = {
  slug: '', nome: '', papel: '', bio: '', foto: '', url: '',
  classe: '', weapon: '', level: 1, hp: 100, xp: 0, since: new Date().getFullYear(),
  skills: [], ordem: 999,
};

function SocioEditPage() {
  const { slug } = Route.useParams();
  const isNew = slug === 'new';
  const { data, isLoading } = useSocio(slug);
  const upsert = useUpsertSocio();
  const setBanner = useUIStore((s) => s.setBanner);
  const banner = useUIStore((s) => s.banner);
  const navigate = useNavigate();

  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (data) setForm({ ...empty, ...data, skills: data.skills ?? [] });
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);
    try {
      await upsert.mutateAsync(form);
      setBanner({ type: 'ok', message: `Salvo: ${form.nome}` });
      if (isNew) navigate({ to: '/socios/$slug', params: { slug: form.slug } });
    } catch (err: any) {
      setBanner({ type: 'error', message: err.message });
    }
  };

  if (!isNew && isLoading) return <p><span className="spinner" /> Carregando…</p>;

  return (
    <>
      <p><Link to="/socios">← Sócios</Link></p>
      <h1>{isNew ? 'Novo sócio' : `Editar · ${form.nome}`}</h1>

      {banner && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem',
          background: banner.type === 'ok' ? '#d1fae5' : '#fee2e2',
          color: banner.type === 'ok' ? '#065f46' : '#991b1b',
        }}>{banner.message}</div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem', maxWidth: 720 }}>
        {isNew && <Field label="Slug" name="slug" form={form} setForm={setForm} required />}
        <Field label="Nome" name="nome" form={form} setForm={setForm} required />
        <Field label="Papel" name="papel" form={form} setForm={setForm} required />
        <Field label="Classe" name="classe" form={form} setForm={setForm} required />
        <Field label="Weapon" name="weapon" form={form} setForm={setForm} required />
        <Field label="Bio" name="bio" form={form} setForm={setForm} required textarea />
        <Field label="Foto (URL)" name="foto" form={form} setForm={setForm} required />
        <Field label="URL externa" name="url" form={form} setForm={setForm} type="url" />
        <Field label="Level" name="level" form={form} setForm={setForm} type="number" />
        <Field label="HP" name="hp" form={form} setForm={setForm} type="number" />
        <Field label="XP" name="xp" form={form} setForm={setForm} type="number" />
        <Field label="Since" name="since" form={form} setForm={setForm} type="number" />
        <Field label="Skills (CSV)" name="skills" form={{ skills: (form.skills ?? []).join(', ') }} setForm={(updater: any) => {
          const next = typeof updater === 'function' ? updater(form) : updater;
          setForm({ ...form, skills: String(next.skills ?? '').split(',').map((s: string) => s.trim()).filter(Boolean) });
        }} />
        <Field label="Ordem" name="ordem" form={form} setForm={setForm} type="number" />

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="submit" className="btn" disabled={upsert.isPending}>
            {upsert.isPending ? <><span className="spinner" /> Salvando…</> : 'Salvar'}
          </button>
          <Link to="/socios" className="btn btn-ghost">Cancelar</Link>
        </div>
      </form>
    </>
  );
}

function Field({ label, name, form, setForm, type = 'text', required, textarea }: any) {
  const value = form[name] ?? '';
  const onChange = (e: any) => setForm((f: any) => ({ ...f, [name]: e.target.value }));
  return (
    <label>
      {label}
      {textarea ? (
        <textarea value={value} onChange={onChange} required={required} rows={3} style={{ width: '100%' }} />
      ) : (
        <input value={value} onChange={onChange} type={type} required={required} style={{ width: '100%' }} />
      )}
    </label>
  );
}
